// useLayerOpacity — engine のレイヤー表示濃度を React へ橋渡しする (両レイアウト共通)。
// 濃さは engine が持ち (永続しない)、ここは購読 + 操作窓口 + 「初めて薄くした」告知だけ。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { CanvasDrawingEngine } from '../../../infrastructure/drawing/CanvasDrawingEngine.js';
import {
  LAYER_OPACITY_NOTICE_STORAGE_KEY,
  LAYER_OPACITY_NOTICE_TEXT,
  type LayerOpacityControl,
} from './layerOpacity.js';

const readSeen = (): boolean => {
  try {
    return window.localStorage.getItem(LAYER_OPACITY_NOTICE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};
const writeSeen = (): void => {
  try {
    window.localStorage.setItem(LAYER_OPACITY_NOTICE_STORAGE_KEY, '1');
  } catch {
    // storage 不可 (private mode 等) はセッション内 1 回 (ref) だけで足りる
  }
};

export interface UseLayerOpacityResult {
  /** index = layerId。ドラッグ中はライブ値。 */
  readonly opacities: readonly number[];
  readonly control: LayerOpacityControl | null;
  /** 全層 100% に戻す (キャンバス隅の札)。 */
  readonly reset: () => void;
}

export const useLayerOpacity = (
  engine: CanvasDrawingEngine | null,
  pushSystemLine: (text: string) => void,
): UseLayerOpacityResult => {
  const [opacities, setOpacities] = useState<readonly number[]>([]);
  useEffect(() => {
    if (engine === null) {
      setOpacities([]);
      return;
    }
    setOpacities(engine.getLayerOpacities());
    return engine.onLayerOpacityChange(setOpacities);
  }, [engine]);

  const noticedRef = useRef(false);
  const pushRef = useRef(pushSystemLine);
  pushRef.current = pushSystemLine;
  const maybeNotice = useCallback((eng: CanvasDrawingEngine): void => {
    if (noticedRef.current || !eng.hasDimmedLayer()) return;
    noticedRef.current = true;
    if (readSeen()) return;
    writeSeen();
    pushRef.current(LAYER_OPACITY_NOTICE_TEXT);
  }, []);

  const control = useMemo<LayerOpacityControl | null>(() => {
    if (engine === null) return null;
    return {
      begin: (id) => engine.beginLayerOpacityPreview(id),
      update: (v) => engine.updateLayerOpacityPreview(v),
      end: (commit) => {
        engine.endLayerOpacityPreview(commit);
        if (commit) maybeNotice(engine);
      },
      set: (id, v) => {
        engine.setLayerOpacity(id, v);
        maybeNotice(engine);
      },
    };
  }, [engine, maybeNotice]);

  const reset = useCallback((): void => {
    engine?.resetLayerOpacities();
  }, [engine]);

  return { opacities, control, reset };
};
